import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import PunchCardLogo from "../../components/logos/PunchCardLogo";
import Container from "../../components/Container";
import { Text } from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface AdminHeaderProps {
  onSignOut?: () => void;
  isSigningOut?: boolean;
}

const AdminHeader: React.FunctionComponent<AdminHeaderProps> = ({
  onSignOut,
  isSigningOut = false,
}) => {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isWorkspacesActive =
    location.pathname === "/admin" ||
    location.pathname.startsWith("/admin/workspaces");
  const isSidekiqActive = location.pathname.startsWith("/admin/sidekiq");
  const navLinkClassName = (isActive: boolean) =>
    cn(
      "rounded-sm px-3 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground",
      isActive
        ? "bg-accent text-accent-foreground"
        : "text-muted-foreground",
    );

  return (
    <header
      className={cn(
        "py-4 border-t-4 border-t-highlight fixed top-0 left-0 right-0 z-50 border-b",
        isScrolled
          ? "bg-background/90 backdrop-blur-md border-b-border shadow-sm transition-[background-color,border-color,box-shadow] duration-300"
          : "bg-gradient-to-b from-background/70 to-transparent border-b-transparent",
      )}
    >
      <Container className="max-w-none flex items-center justify-between">
        <div className="flex items-center gap-6">
          <Link
            to="/admin/workspaces"
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
          >
            <PunchCardLogo />
            <Text variant="sm" className="text-muted-foreground font-semibold uppercase tracking-wide">
              Admin
            </Text>
          </Link>

          <nav className="flex items-center gap-1" aria-label="Admin">
            <Link
              to="/admin/workspaces"
              className={navLinkClassName(isWorkspacesActive)}
              aria-current={isWorkspacesActive ? "page" : undefined}
            >
              Workspaces
            </Link>
            <a
              href="/admin/sidekiq"
              className={navLinkClassName(isSidekiqActive)}
              aria-current={isSidekiqActive ? "page" : undefined}
            >
              Sidekiq
            </a>
          </nav>
        </div>

        {onSignOut && (
          <Button
            variant="outline"
            onClick={onSignOut}
            disabled={isSigningOut}
          >
            {isSigningOut ? "Signing out..." : "Sign out"}
          </Button>
        )}
      </Container>
    </header>
  );
};

export default AdminHeader;
